import styled from '@emotion/styled';
import { HTMLAttributes, ReactNode } from 'react';
import { Rate } from 'antd';
import { useTranslate } from '@tolgee/react';
import upSvg from '../assets/up.svg';
import assistantAvatarImage from '../assets/assistantAvatar.png';
import buyerAvatarSvg from '../assets/buyerAvatar.svg';

const Container = styled.div`
  width: 320px;
  border: solid 1px #d3d9e9;
  border-radius: 28px;
`;

const PositiveContainer = styled(Container)`
  display: flex;
  padding: 20px 17px;
  background: #fff;
  > span {
    flex: 0 0 32px;
    width: 32px;
    height: 32px;
    margin: 4px 8px 0 0;
    border-radius: 16px;
    background: #f0f2f5 no-repeat center / 100% url(${buyerAvatarSvg});
  }
  > div {
    flex: 1 0 0;
  }
`;

const PositiveNick = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  img {
    width: 14px;
    height: 14px;
  }
`;

const StarRate = styled(Rate)`
  font-size: 14px;
  line-height: 14px;
  > li {
    margin-right: 3px !important;
  }
`;

const PlaceHolder1 = styled.div`
  width: 180px;
  height: 15px;
  margin-top: 6px;
  margin-bottom: 2px;
  background: #f0f2f5;
`;

const PlaceHolder2 = styled.div`
  width: 124px;
  height: 11px;
  margin-bottom: 2px;
  background: #f0f2f5;
`;

const PositiveText = styled.div`
  padding: 8px 10px;
  border-radius: 2px;
  background: #f7f9fa;
  > span {
    font-size: 12px;
    color: #895b27;
  }
`;

const Light = styled.div`
  color: var(--theme-text-3);
`;

export type ResultType = 'positive' | 'negative' | 'refund';

export interface ResultProps extends HTMLAttributes<HTMLDivElement> {
  type: ResultType;
  text: ReactNode | ReactNode[];
}

export function PositiveResult(props: Omit<ResultProps, 'type'> & { stars?: number }) {
  const { text, stars = 5, ...otherProps } = props;

  const { t } = useTranslate();

  const list = Array.isArray(text) ? text : [text];

  return (
    <PositiveContainer {...otherProps}>
      <span />
      <div>
        <PositiveNick>
          <span>{t('ViewBlockList.买家昵称')}</span>
          {stars === 5 && <img src={upSvg} alt="up" />}
        </PositiveNick>
        <StarRate disabled value={stars} />
        <PlaceHolder1 />
        <PlaceHolder2 />
        {list.map((text) => {
          return (
            <PositiveText>
              <span>Seller Response:</span>
              <div>{text || <Light>请输入</Light>}</div>
            </PositiveText>
          );
        })}
      </div>
    </PositiveContainer>
  );
}

const NegativeContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 540px;
  border: solid 6px #fff;
  border-radius: 28px;
  background: #f7f9fa;
  overflow: auto;
  > header {
    flex: 0 0 auto;
    align-self: center;
    position: sticky;
    top: 0;
    width: 164px;
    height: 22px;
    background: #fff;
    border-radius: 0 0 18px 18px;
  }
  > div {
    flex: 1 0 0;
    padding: 26px 14px;
  }
`;

const NegativeContent = styled.div<{ type?: 'reply' | 'query' }>`
  display: flex;
  flex-direction: ${({ type }) => (type === 'query' ? 'row-reverse' : 'row')};
  margin-bottom: 20px;
  > span {
    flex: 0 0 32px;
    width: 32px;
    height: 32px;
    margin-right: ${({ type }) => (type === 'query' ? 0 : '8px')};
    margin-left: ${({ type }) => (type === 'query' ? '8px' : 0)};
    border-radius: 16px;
    background: #f0f2f5 no-repeat center / 100%
      url(${({ type }) => (type === 'query' ? buyerAvatarSvg : assistantAvatarImage)});
  }
  > div {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    align-items: ${({ type }) => (type === 'query' ? 'flex-end' : 'flex-start')};
    > span {
      font-size: 12px;
    }
  }
  > div > div {
    padding: 12px;
    background: #fff;
    border-radius: 4px;
  }
`;

export function NegativeResult(props: Omit<ResultProps, 'type'>) {
  const { text, ...otherProps } = props;

  const { t } = useTranslate();

  const list = Array.isArray(text) ? text : [text];

  return (
    <NegativeContainer {...otherProps}>
      <header>{t('phrase.店铺名称')}</header>
      <div>
        {list.map((text) => {
          return (
            <NegativeContent>
              <span />
              <div>
                <span>{t('refundSetting.客服昵称')}</span>
                <div>{text || <Light>{t('refundSetting.请输入')}</Light>}</div>
              </div>
            </NegativeContent>
          );
        })}
      </div>
    </NegativeContainer>
  );
}

export interface ScenarioResultProps extends HTMLAttributes<HTMLDivElement> {
  list: {
    type: 'system' | 'reply' | 'query';
    content: ReactNode;
  }[];
}

const ScenarioSystemMessage = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  > div {
    max-width: 85%;
    line-height: 26px;
    font-size: 12px;
    padding: 0 10px;
    background: #efefef;
    color: #515359;
    border-radius: 13px;
    word-break: break-all;
  }
`;

export function ScenarioResult(props: ScenarioResultProps) {
  const { list, ...otherProps } = props;

  const { t } = useTranslate();

  return (
    <Container>
      <NegativeContainer {...otherProps}>
        <header />
        <div>
          {list.map(({ type, content }) => {
            if (type === 'reply') {
              return (
                <NegativeContent>
                  <span />
                  <div>
                    <span>{t('refundSetting.客服昵称')}</span>
                    <div>{content || <Light>{t('refundSetting.请输入')}</Light>}</div>
                  </div>
                </NegativeContent>
              );
            }

            if (type === 'query') {
              return (
                <NegativeContent type="query">
                  <span />
                  <div>
                    <span>{t('marketing.买家昵称')}</span>
                    <div>{content}</div>
                  </div>
                </NegativeContent>
              );
            }

            if (type === 'system') {
              return (
                <ScenarioSystemMessage>
                  <div>{content}</div>
                </ScenarioSystemMessage>
              );
            }

            return null;
          })}
        </div>
      </NegativeContainer>
    </Container>
  );
}

const SystemMessage = styled.div`
  padding: 0 20px;
  margin-bottom: 8px;
  font-size: 12px;
  text-align: center;
  color: #515359;
`;

export function RefundResult(props: Omit<ResultProps, 'type'>) {
  const { text, ...otherProps } = props;

  const { t } = useTranslate();

  const list = Array.isArray(text) ? text : [text];

  return (
    <NegativeContainer {...otherProps}>
      <header>{t('phrase.店铺名称')}</header>
      <div>
        <SystemMessage>00:12:32</SystemMessage>
        <SystemMessage>{t('refundSetting.买家申请取消订单，系统已自动同意')}</SystemMessage>
        {list.map(() => {
          return (
            <NegativeContent>
              <span />
              <div>
                <span>{t('refundSetting.客服昵称')}</span>
                <div>{text || <Light>请输入</Light>}</div>
              </div>
            </NegativeContent>
          );
        })}
      </div>
    </NegativeContainer>
  );
}

function Result(props: ResultProps) {
  const { type, ...otherProps } = props;

  const typeComponentMap = {
    positive: PositiveResult,
    negative: NegativeResult,
    refund: RefundResult,
  } as const;

  const { [type]: Component } = typeComponentMap;

  return <Component {...otherProps} />;
}

export default Result;
